/**
 * Local Community Selector component
 * ==================================
 *
 * Is used for community selection. Examples of usage could be found on profile
 * page and registration (community selection page)
 *
 * @options
 * - model: model that contains coordinates ({latitude, longitude})
 *          to by applied for the search
 * - [communityModel]: currently selected community (is rendered in input)
 *                If not provided - empty community will be created and the placeholder
 *                will be shown
 * - [nearbyCommunitiesModel] - nearby communities search model
 *
 * @public
 * render() - render component
 * setState() - set component state, see available states below
 *
 * @states
 * - disabled - disable/enable component
 *
 * @example
 * var LocalcommunitySelectorComponent = require('component/community_selector/localcommunity_selector');
 * var homeCommunityModel = require('shared/model/home_community');
 * var community = homeCommunityModel.clone();
 * var currentUserLocationModel = require('shared/model/current_user_location');
 *
 * // instantiate Local Community Selector after user coordinats are retrieved
 * currentUserLocationModel.fetchOnce().then(function() {
 *   View.create(LocalcommunitySelectorComponent, {
 *      el: '.js-local-community-selector',
 *      model: currentUserLocationModel
 *   });
 * });
 */

import _ from 'lodash'
import View from 'view/view'
// Models
import Model from 'model/model'
import CommunitiesSearchModel from 'component/quick_search/model/communities_search'
import CommunityModel from 'model/community'
// Views
import PopoverView from 'component/popover/popover'
import CommunityFlyout from 'component/community_selector/view/localcommunity_flyout'
import LocalcommunitySelectFieldView from 'component/community_selector/view/localcommunity_select_field'
// Templates
import template from 'component/community_selector/template/localcommunity.tmpl'

const LocalcommunitySelector = View.extend({
    className: 'js-local-community-selector-component',

    events: {},

    template,

    bubbleEvents: {
        'Popover:open': '_onPopoverOpen',
        'Popover:close': '_onPopoverClose',

        'CommunitySearchResult:select': '_onCommunitySearchResultSelect',
        'CommunityFlyout:closePopover': '_onFlyoutAskedToClose',

        'LocalcommunitySelectField:click': '_onSelectFieldClick',
    },

    initialize(options) {
        options = options || {}

        // if nearby communities not provided - create default one
        const nearbyCommunitiesModel = options.nearbyCommunitiesModel
            ? options.nearbyCommunitiesModel
            : new CommunitiesSearchModel({
                  coordinates: this.model.get('coordinates'),
              })

        this._subviews = {}
        this._model = {
            communitiesSearchNearby: nearbyCommunitiesModel,
            communitiesSearch: new CommunitiesSearchModel({
                coordinates: this.model.get('coordinates'),
                minLength: 2,
            }),
        }

        this._initializeFlyout = _.once(this._initializeFlyout)
        this._initializePopover = _.once(this._initializePopover)

        this._model.community = options.communityModel || new CommunityModel()

        // component state model
        this._model.state = new Model({
            disabled: options.disabled,
        })

        this.listenTo(this.model, 'change:coordinates', function(model) {
            const coordinates = model.get('coordinates')

            this._model.communitiesSearchNearby.set('coordinates', coordinates)
            this._model.communitiesSearch.set('coordinates', coordinates)
        })

        this.listenTo(this._model.state, 'change', this.render)
    },

    _closePopover() {
        if (!this._subviews.popover) {
            return
        }

        this._subviews.popover.close()
    },

    _onSelectFieldClick() {
        // lazy popover initialize

        if (!this._subviews.popover) {
            this._subviews.popover = this._initializePopover()

            this._subviews.popover.toggle()
        }
    },

    render() {
        this.destroySubviews()

        this.$el.html(this.template())

        this._subviews.selectField = this.initSubview(
            LocalcommunitySelectFieldView,
            {
                el: this.$('.js-local-community-select-field'),
                model: this._model.community,
                disabled: this._model.state.get('disabled'),
            },
            { remove: 'content' }
        ).render()

        return this
    },

    _initializePopover() {
        this._subviews.flyout = this._initializeFlyout()

        return this.initSubview(PopoverView, {
            el: this.$('.js-local-community-select-field'),
            extraClasses: 'popover-localcommunitySelector',
            content: this._subviews.flyout.el,
            preset: 'dropdownWithClose',
        })
    },

    _initializeFlyout() {
        return this.initSubview(CommunityFlyout, {
            communitiesSearchNearbyModel: this._model.communitiesSearchNearby,
            communitiesSearchModel: this._model.communitiesSearch,
        }).render()
    },

    _onPopoverOpen() {
        this._subviews.flyout.focus()
        this._subviews.selectField.setState({ open: true })
    },

    _onPopoverClose() {
        this._subviews.flyout.resetInputValue()
        this._subviews.selectField.setState({ open: false })
    },

    /**
     * Toggle flyout. Is needed for toggling from events, because there
     * event object is provided as arguments
     */
    _onFlyoutAskedToClose() {
        this._subviews.popover.close()
    },

    _onCommunitySearchResultSelect(payload) {
        this._closePopover()

        // do not react if you select same community
        if (payload.community.get('id') === this._model.community.get('id')) {
            return
        }

        this._model.community.reset(payload.community.toJSON())

        if (typeof this.options.onChangeCallback === 'function') {
            this.options.onChangeCallback(payload.community.toJSON())
        }
    },

    setState(state) {
        this._model.state.set(state)

        return this
    },
})

export default LocalcommunitySelector
